import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@ouropix/ui';
import { Input } from '@ouropix/ui';
import { Badge } from '@ouropix/ui';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ouropix/ui';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@ouropix/ui';
import { ConfirmModal } from '@ouropix/ui';
import { toast } from 'sonner';
import { apiErrorMessage } from '@/lib/api-helpers';
import {
  FolderOpen,
  Folder,
  Search,
  Grid3X3,
  List,
  Home,
  ChevronRight,
  Trash2,
  XCircle,
  CheckSquare,
  Square,
  Download,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  FileText,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { api } from '@/lib/api';
import { FolderTree } from '@/components/media/FolderTree';
import { MediaGrid } from '@/components/media/MediaGrid';
import { MediaUploader } from '@/components/media/MediaUploader';
import { MediaStatsCard } from '@/components/media/MediaStatsCard';
import { useMediaLibrary } from '@/hooks/useMediaLibrary';
import { useMediaStats } from '@/hooks/useMediaStats';
import { useMediaBatchDelete } from '@/hooks/useMediaBatchDelete';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_admin/media/')({
  component: MediaLibraryPage,
});

/** Shape normalizada para a UI (compativel com MediaGrid) */
interface MediaFile {
  id: string;
  filename: string;
  file_path: string;
  file_url: string;
  file_type: string;
  file_size: number;
  folder_id: string | null;
  created_at: string;
}

interface MediaFolder {
  id: string;
  name: string;
  parent_id: string | null;
  created_at: string;
}

type SortOption = 'newest' | 'oldest' | 'az' | 'za' | 'largest' | 'smallest';
type ViewMode = 'grid' | 'list';
type TypeFilter = 'all' | 'image' | 'video' | 'document';

function formatSize(bytes: number) {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

function matchesType(file: MediaFile, filter: TypeFilter) {
  if (filter === 'all') return true;
  if (filter === 'image') return file.file_type?.startsWith('image/');
  if (filter === 'video') return file.file_type?.startsWith('video/');
  return !file.file_type?.startsWith('image/') && !file.file_type?.startsWith('video/');
}

function sortFiles(files: MediaFile[], sort: SortOption): MediaFile[] {
  return [...files].sort((a, b) => {
    switch (sort) {
      case 'newest': return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      case 'oldest': return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      case 'az': return a.filename.localeCompare(b.filename);
      case 'za': return b.filename.localeCompare(a.filename);
      case 'largest': return (b.file_size || 0) - (a.file_size || 0);
      case 'smallest': return (a.file_size || 0) - (b.file_size || 0);
      default: return 0;
    }
  });
}

function MediaLibraryPage() {
  const queryClient = useQueryClient();

  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Dialogs
  const [previewFile, setPreviewFile] = useState<MediaFile | null>(null);
  const [renameFile, setRenameFile] = useState<MediaFile | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<MediaFile[] | null>(null);

  // --- Hooks ---
  const { data: statsData, isLoading: statsLoading } = useMediaStats();
  const { data: mediaLibraryData, isLoading: filesLoading } = useMediaLibrary({
    folderId: currentFolderId,
  });

  const batchDelete = useMediaBatchDelete();

  // Folders — endpoint separado sem hook proprio.
  // gRPC retorna camelCase (`parentId`); FolderTree usa snake_case (`parent_id`).
  // Sem normalizar, todos folders sao tratados como root (subpastas somem).
  const { data: allFolders = [] } = useQuery<MediaFolder[]>({
    queryKey: ['media-folders'],
    queryFn: async () => {
      const { data } = await api.get('/media/folders');
      // Backend retorna { folders: [...] } com camelCase (parentId, createdAt) vindo do gRPC mapping.
      // FolderTree espera snake_case (parent_id, created_at) — normalizamos aqui.
      const raw =
        Array.isArray(data?.folders) ? data.folders :
        Array.isArray(data?.items) ? data.items :
        Array.isArray(data) ? data : [];
      return raw.map((f: any) => ({
        id: f.id,
        name: f.name,
        parent_id: f.parent_id ?? (f.parentId && f.parentId.length > 0 ? f.parentId : null),
        created_at: f.created_at ?? f.createdAt ?? '',
      }));
    },
  });

  // Normaliza items do gRPC (camelCase) para shape da UI (snake_case)
  const files: MediaFile[] = useMemo(() => {
    const raw = mediaLibraryData?.items ?? [];
    return raw.map((item) => ({
      id: item.id,
      filename: item.filename,
      file_path: '',
      file_url: item.url,
      file_type: item.mimeType,
      file_size: item.sizeBytes,
      folder_id: item.folderId || null,
      created_at: item.createdAt,
    }));
  }, [mediaLibraryData]);

  const breadcrumb = useMemo(() => {
    const path: MediaFolder[] = [];
    let id = currentFolderId;
    while (id) {
      const folder = allFolders.find((f) => f.id === id);
      if (!folder) break;
      path.unshift(folder);
      id = folder.parent_id;
    }
    return path;
  }, [currentFolderId, allFolders]);

  const filteredFiles = useMemo(() => {
    let result = files.filter((f) => matchesType(f, typeFilter));
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      result = result.filter((f) => f.filename?.toLowerCase().includes(s));
    }
    return sortFiles(result, sortBy);
  }, [files, typeFilter, searchTerm, sortBy]);

  // Subpastas filhas diretas da pasta atual — mostradas no grid/lista junto com arquivos.
  // Quando ha busca ativa, esconde (busca aplica so em arquivos por enquanto).
  const currentSubfolders = useMemo(() => {
    if (searchTerm) return [];
    const subs = allFolders.filter((f) => f.parent_id === currentFolderId);
    return [...subs].sort((a, b) => a.name.localeCompare(b.name));
  }, [allFolders, currentFolderId, searchTerm]);

  // Conta arquivos por pasta filha (mostra hint no card)
  const filesPerFolder = useMemo(() => {
    const map = new Map<string, number>();
    for (const f of files) {
      if (f.folder_id) map.set(f.folder_id, (map.get(f.folder_id) ?? 0) + 1);
    }
    return map;
  }, [files]);

  const renameMutation = useMutation({
    mutationFn: async ({ id, filename }: { id: string; filename: string }) => {
      await api.put(`/media/${id}`, { filename });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-library'] });
      setRenameFile(null);
      toast.success('Arquivo renomeado!');
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Erro ao renomear')),
  });

  const handleDelete = useCallback(
    (ids: string[]) => {
      batchDelete.mutate(ids, {
        onSuccess: () => {
          setDeleteConfirm(null);
          setSelectedIds(new Set());
        },
      });
    },
    [batchDelete],
  );

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(filteredFiles.map((f) => f.id)));
  const clearSelection = () => setSelectedIds(new Set());
  const selectedFiles = filteredFiles.filter((f) => selectedIds.has(f.id));

  // Preview navigation
  const previewIndex = previewFile ? filteredFiles.findIndex((f) => f.id === previewFile.id) : -1;

  const copyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success('URL copiada!');
  };

  const handleFolderSelect = useCallback((id: string | null) => {
    setCurrentFolderId(id);
    clearSelection();
  }, []);

  // Stats para o header badge (compatibilidade com shape antiga totalFiles/totalSizeBytes)
  const headerStats = statsData
    ? { totalFiles: statsData.total_count, totalSizeBytes: statsData.total_bytes }
    : null;

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] -mx-4 -mt-2">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div className="flex items-center gap-3">
          <FolderOpen className="h-5 w-5 text-primary" />
          <h1 className="text-lg font-bold">Biblioteca de Mídias</h1>
          {headerStats && (
            <Badge variant="secondary" className="text-xs">
              {headerStats.totalFiles} arquivo{headerStats.totalFiles !== 1 ? 's' : ''} · {formatSize(headerStats.totalSizeBytes)}
            </Badge>
          )}
        </div>
      </div>

      {/* Stats card */}
      <div className="px-4 pt-3">
        <MediaStatsCard stats={statsData} isLoading={statsLoading} />
      </div>

      {/* Selection bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-2 bg-primary/5 border-b">
          <Badge>{selectedIds.size} selecionado(s)</Badge>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => setDeleteConfirm(selectedFiles)}
            disabled={batchDelete.isPending}
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" />
            Excluir
          </Button>
          <Button size="sm" variant="ghost" onClick={clearSelection}>
            <XCircle className="h-3.5 w-3.5 mr-1" />
            Limpar
          </Button>
        </div>
      )}

      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <FolderTree
          folders={allFolders}
          currentFolderId={currentFolderId}
          onSelect={handleFolderSelect}
        />

        {/* Main content */}
        <div className="flex flex-col flex-1 min-w-0">
          {/* Toolbar */}
          <div className="flex items-center gap-2 px-4 py-2 border-b flex-wrap">
            {/* Breadcrumb */}
            <div className="flex items-center gap-1 text-sm flex-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-1 text-muted-foreground"
                onClick={() => handleFolderSelect(null)}
              >
                <Home className="h-3.5 w-3.5" />
              </Button>
              {breadcrumb.map((folder) => (
                <div key={folder.id} className="flex items-center gap-1">
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-1"
                    onClick={() => handleFolderSelect(folder.id)}
                  >
                    {folder.name}
                  </Button>
                </div>
              ))}
            </div>

            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-7 pl-7 text-sm w-40"
              />
            </div>

            <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as TypeFilter)}>
              <SelectTrigger className="h-7 text-xs w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="image">Imagens</SelectItem>
                <SelectItem value="video">Vídeos</SelectItem>
                <SelectItem value="document">Documentos</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortOption)}>
              <SelectTrigger className="h-7 text-xs w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Mais recentes</SelectItem>
                <SelectItem value="oldest">Mais antigos</SelectItem>
                <SelectItem value="az">A → Z</SelectItem>
                <SelectItem value="za">Z → A</SelectItem>
                <SelectItem value="largest">Maiores</SelectItem>
                <SelectItem value="smallest">Menores</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex items-center border rounded-md overflow-hidden">
              <Button
                variant="ghost"
                size="icon"
                className={cn('h-7 w-7 rounded-none', viewMode === 'grid' && 'bg-muted')}
                onClick={() => setViewMode('grid')}
              >
                <Grid3X3 className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className={cn('h-7 w-7 rounded-none', viewMode === 'list' && 'bg-muted')}
                onClick={() => setViewMode('list')}
              >
                <List className="h-3.5 w-3.5" />
              </Button>
            </div>

            {filteredFiles.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={selectedIds.size === filteredFiles.length ? clearSelection : selectAll}
              >
                {selectedIds.size === filteredFiles.length ? (
                  <><Square className="h-3.5 w-3.5 mr-1" />Desmarcar</>
                ) : (
                  <><CheckSquare className="h-3.5 w-3.5 mr-1" />Selecionar tudo</>
                )}
              </Button>
            )}
          </div>

          {/* Upload area */}
          <div className="px-4 py-3 border-b">
            <MediaUploader currentFolderId={currentFolderId} />
          </div>

          {/* Subpastas + Arquivos */}
          <div className="flex-1 overflow-auto">
            {currentSubfolders.length > 0 && (
              <div className="px-4 pt-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                  Pastas ({currentSubfolders.length})
                </p>
                {viewMode === 'grid' ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {currentSubfolders.map((sf) => {
                      const fileCount = filesPerFolder.get(sf.id) ?? 0;
                      const subCount = allFolders.filter((f) => f.parent_id === sf.id).length;
                      return (
                        <button
                          key={sf.id}
                          type="button"
                          onClick={() => handleFolderSelect(sf.id)}
                          className="group flex flex-col items-center gap-2 p-4 rounded-lg border bg-muted/30 hover:bg-muted hover:border-primary transition-colors text-left"
                        >
                          <Folder className="h-12 w-12 text-primary" />
                          <span className="text-sm font-medium truncate w-full text-center">{sf.name}</span>
                          {(fileCount > 0 || subCount > 0) && (
                            <span className="text-xs text-muted-foreground">
                              {subCount > 0 && `${subCount} pasta${subCount > 1 ? 's' : ''}`}
                              {subCount > 0 && fileCount > 0 && ' · '}
                              {fileCount > 0 && `${fileCount} arquivo${fileCount > 1 ? 's' : ''}`}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="border rounded-md divide-y">
                    {currentSubfolders.map((sf) => {
                      const fileCount = filesPerFolder.get(sf.id) ?? 0;
                      const subCount = allFolders.filter((f) => f.parent_id === sf.id).length;
                      return (
                        <button
                          key={sf.id}
                          type="button"
                          onClick={() => handleFolderSelect(sf.id)}
                          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-muted text-left"
                        >
                          <Folder className="h-5 w-5 text-primary shrink-0" />
                          <span className="text-sm font-medium flex-1 truncate">{sf.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {subCount > 0 && `${subCount} pasta${subCount > 1 ? 's' : ''}`}
                            {subCount > 0 && fileCount > 0 && ' · '}
                            {fileCount > 0 && `${fileCount} arquivo${fileCount > 1 ? 's' : ''}`}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
                {filteredFiles.length > 0 && (
                  <p className="text-xs font-semibold text-muted-foreground uppercase mb-2 mt-6">
                    Arquivos ({filteredFiles.length})
                  </p>
                )}
              </div>
            )}
            <MediaGrid
              files={filteredFiles}
              isLoading={filesLoading}
              viewMode={viewMode}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onPreview={setPreviewFile}
              onRename={(file) => {
                setRenameFile(file);
                setRenameValue(file.filename);
              }}
              onDelete={(files) => setDeleteConfirm(files)}
            />
          </div>
        </div>
      </div>

      {/* Preview modal */}
      <Dialog open={!!previewFile} onOpenChange={(o) => !o && setPreviewFile(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="truncate">{previewFile?.filename}</span>
            </DialogTitle>
          </DialogHeader>
          {previewFile && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={previewIndex <= 0}
                  onClick={() => setPreviewFile(filteredFiles[previewIndex - 1])}
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <div className="flex-1 mx-4">
                  {previewFile.file_type?.startsWith('image/') ? (
                    <img
                      src={previewFile.file_url}
                      alt={previewFile.filename}
                      className="max-h-[50vh] mx-auto object-contain rounded-lg"
                    />
                  ) : previewFile.file_type?.startsWith('video/') ? (
                    <video controls className="max-h-[50vh] w-full rounded-lg">
                      <source src={previewFile.file_url} />
                    </video>
                  ) : (
                    <div className="flex items-center justify-center h-40 bg-muted rounded-lg">
                      <FileText className="h-12 w-12 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={previewIndex >= filteredFiles.length - 1}
                  onClick={() => setPreviewFile(filteredFiles[previewIndex + 1])}
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-muted-foreground">Tipo</p>
                  <p>{previewFile.file_type}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Tamanho</p>
                  <p>{formatSize(previewFile.file_size)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Criado em</p>
                  <p>{format(new Date(previewFile.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}</p>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => copyUrl(previewFile.file_url)}
                >
                  Copiar URL
                </Button>
                <Button variant="outline" asChild>
                  <a href={previewFile.file_url} download target="_blank" rel="noopener noreferrer">
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </a>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={!!renameFile} onOpenChange={(o) => !o && setRenameFile(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Renomear arquivo</DialogTitle>
          </DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && renameFile && renameValue.trim()) {
                renameMutation.mutate({ id: renameFile.id, filename: renameValue.trim() });
              }
            }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameFile(null)}>Cancelar</Button>
            <Button
              onClick={() => {
                if (renameFile && renameValue.trim()) {
                  renameMutation.mutate({ id: renameFile.id, filename: renameValue.trim() });
                }
              }}
              disabled={renameMutation.isPending}
            >
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm — usa useMediaBatchDelete */}
      <ConfirmModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => {
          if (deleteConfirm) {
            handleDelete(deleteConfirm.map((f) => f.id));
          }
        }}
        title={`Excluir arquivo${deleteConfirm && deleteConfirm.length > 1 ? 's' : ''}`}
        description={
          deleteConfirm && deleteConfirm.length === 1
            ? `Tem certeza que deseja excluir "${deleteConfirm[0].filename}"? Esta ação não pode ser desfeita.`
            : `Tem certeza que deseja excluir ${deleteConfirm?.length} arquivo(s)? Esta ação não pode ser desfeita.`
        }
        confirmText="Excluir"
        cancelText="Cancelar"
        variant="destructive"
        isLoading={batchDelete.isPending}
      />
    </div>
  );
}
